import {
  CLAVES_REGLAS_ALERTAS,
  NOMBRE_CANAL_CORREO,
  NOMBRE_REGLA_ALERTA,
  type ReglasAlertas,
  TIPOS_NOTIFICACION,
  type Urgencia,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { conAviso, queryClient, useTRPC } from "../api.ts";
import { Encabezado, Kpi } from "../componentes/Encabezado.tsx";
import {
  Aviso,
  Boton,
  BotonEnlace,
  CLASE_ENTRADA,
  Enlace,
  EstadoVacio,
  Tarjeta,
} from "../componentes/ui.tsx";
import type { FilaAlerta } from "../tipos.ts";
import { mensajeDe } from "../utiles.ts";

const GRUPOS: { urgencia: Urgencia; titulo: string }[] = [
  { urgencia: "vencida", titulo: "Vencidas — actuar hoy" },
  { urgencia: "semana", titulo: "Esta semana" },
  { urgencia: "mes", titulo: "Este mes" },
  { urgencia: "despues", titulo: "Más adelante" },
];

const TONO_BADGE: Record<FilaAlerta["tipo"], string> = {
  revision_anual: "bg-marino-claro text-marino",
  retencion: "bg-ambar-fondo text-ambar",
  firma_pendiente: "bg-gris-fondo text-gris",
  acuses_incompletos: "bg-gris-fondo text-gris",
};

/**
 * Pantalla 08 · Alertas de vencimiento. "La alerta nace sola en la fecha de vencimiento menos
 * los días de anticipación. Nadie tiene que acordarse." Cada fila trae su acción: no hay un
 * botón genérico.
 */
export function PaginaAlertas() {
  const trpc = useTRPC();
  const lista = useQuery(trpc.alertas.listar.queryOptions());
  const [error, setError] = useState<string | null>(null);

  const opciones = conAviso(setError);
  const ratificar = useMutation(trpc.alertas.ratificar.mutationOptions(opciones));
  const disponer = useMutation(trpc.alertas.registrarDisposicion.mutationOptions(opciones));
  const recordar = useMutation(trpc.alertas.recordar.mutationOptions(opciones));
  const ocupado = ratificar.isPending || disponer.isPending || recordar.isPending;

  function actuar(f: FilaAlerta, accion: FilaAlerta["acciones"][number]["accion"]) {
    if (accion === "ratificar") {
      const ok = window.confirm(
        `Ratificar ${f.documento.codigo}: confirmas que lo revisaste y sigue valiendo tal como está. La próxima revisión se corre un año.`,
      );
      if (ok) ratificar.mutate({ revisionId: f.revision.id });
    } else if (accion === "registrar_disposicion") {
      const ok = window.confirm(
        `Registrar la disposición de ${f.documento.codigo}: el registro cumplió su retención y ya se destruyó o pasó a archivo pasivo. No se puede deshacer.`,
      );
      if (ok) disponer.mutate({ revisionId: f.revision.id });
    } else if (accion === "recordar") {
      recordar.mutate({ alertaId: f.id });
    }
  }

  const d = lista.data;
  return (
    <div>
      <Encabezado
        overline="Sistema"
        titulo="Alertas de vencimiento"
        acciones={
          lista.data?.puedeExportar && (
            <Enlace href="/reportes/auditoria.xlsx">Exportar plan de acción</Enlace>
          )
        }
      />
      {lista.isPending && <EstadoVacio>Cargando…</EstadoVacio>}
      {lista.error && <Aviso>{mensajeDe(lista.error)}</Aviso>}
      {d && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi valor={d.resumen.vencidas} etiqueta="Vencidas — actuar hoy" tono="rojo" />
            <Kpi valor={d.resumen.semana} etiqueta="Vencen esta semana" tono="ambar" />
            <Kpi valor={d.resumen.mes} etiqueta="Vencen este mes" />
            <Kpi valor={d.resumen.firmasPendientes} etiqueta="Firmas pendientes" />
          </div>

          {error && <Aviso className="mb-4">{error}</Aviso>}

          <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
            <div className="space-y-6">
              {d.alertas.length === 0 && (
                <EstadoVacio>No hay alertas abiertas en los procesos que ves.</EstadoVacio>
              )}
              {GRUPOS.map(({ urgencia, titulo }) => {
                const filas = d.alertas.filter((f) => f.urgencia === urgencia);
                if (filas.length === 0) return null;
                return (
                  <section key={urgencia}>
                    <h2 className="mb-2 text-[13px] font-bold text-marino">
                      {titulo} <span className="font-normal text-texto-3">({filas.length})</span>
                    </h2>
                    <ul className="divide-y divide-borde/60 rounded-tarjeta border border-borde bg-white">
                      {filas.map((f) => (
                        <li
                          key={f.id}
                          className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
                        >
                          <span
                            className={`w-[150px] shrink-0 rounded-full px-2.5 py-0.5 text-center text-[11.5px] font-semibold ${
                              f.vencida && f.tipo !== "acuses_incompletos"
                                ? "bg-rojo-fondo text-rojo"
                                : TONO_BADGE[f.tipo]
                            }`}
                          >
                            {f.badge}
                          </span>
                          <div className="min-w-[220px] flex-1">
                            <Link
                              to="/documentos/$codigo"
                              params={{ codigo: f.documento.codigo }}
                              className="font-semibold hover:underline"
                            >
                              <span className="font-mono text-[12px] text-marino">
                                {f.documento.codigo}
                              </span>{" "}
                              · {f.documento.nombre}
                            </Link>
                            <div className="text-[11.5px] text-texto-3">{f.detalle}</div>
                          </div>
                          <div className="w-[170px] shrink-0 text-[12px] text-texto-2">
                            {f.responsable}
                          </div>
                          <div className="flex shrink-0 flex-wrap justify-end gap-2">
                            {f.acciones.map((x) =>
                              x.accion === "crear_revision" ? (
                                <BotonEnlace
                                  key={x.accion}
                                  to="/documentos/$codigo"
                                  params={{ codigo: f.documento.codigo }}
                                  variante="primario"
                                >
                                  {x.etiqueta}
                                </BotonEnlace>
                              ) : (
                                <Boton
                                  key={x.accion}
                                  variante="secundario"
                                  disabled={ocupado}
                                  onClick={() => actuar(f, x.accion)}
                                >
                                  {x.etiqueta}
                                </Boton>
                              ),
                            )}
                            {f.acciones.length === 0 && f.tipo === "retencion" && !f.vencida && (
                              <span className="text-[11.5px] text-texto-3">Programada</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>

            <PanelReglas puedeAdministrar={d.puedeAdministrar} />
          </div>
        </>
      )}
    </div>
  );
}

/** Panel "Cómo y cuándo avisar": lo ve todo el mundo, lo cambia un administrador. */
function PanelReglas({ puedeAdministrar }: { puedeAdministrar: boolean }) {
  const trpc = useTRPC();
  const reglas = useQuery(trpc.alertas.reglas.queryOptions());
  const [borrador, setBorrador] = useState<ReglasAlertas | null>(null);
  useEffect(() => {
    if (reglas.data) setBorrador(reglas.data);
  }, [reglas.data]);
  const guardar = useMutation(
    trpc.alertas.guardarReglas.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries(),
    }),
  );
  const cambio =
    !!borrador &&
    !!reglas.data &&
    CLAVES_REGLAS_ALERTAS.some((c) => borrador[c] !== reglas.data[c]);

  return (
    <Tarjeta titulo="Cómo y cuándo avisar">
      <p className="mb-3 border-l-[3px] border-marino pl-3 text-[12px] text-texto-2">
        La alerta nace sola en la fecha de vencimiento menos los días de anticipación. Nadie tiene
        que acordarse.
      </p>
      {borrador && (
        <div className="space-y-2.5">
          {CLAVES_REGLAS_ALERTAS.map((clave) => (
            <label key={clave} className="flex items-center gap-2 text-[12.5px]">
              <span className="flex-1 text-texto-2">{NOMBRE_REGLA_ALERTA[clave].etiqueta}</span>
              <input
                type="number"
                min={1}
                max={365}
                disabled={!puedeAdministrar}
                value={borrador[clave]}
                onChange={(e) => setBorrador({ ...borrador, [clave]: Number(e.target.value) || 1 })}
                className={`${CLASE_ENTRADA} !w-[64px] text-right`}
              />
              <span className="w-[64px] text-[11.5px] text-texto-3">
                {NOMBRE_REGLA_ALERTA[clave].unidad}
              </span>
            </label>
          ))}
        </div>
      )}
      {guardar.error && <Aviso className="mt-3">{mensajeDe(guardar.error)}</Aviso>}
      {puedeAdministrar ? (
        <div className="mt-3 flex justify-end">
          <Boton
            disabled={!cambio || guardar.isPending}
            onClick={() => borrador && guardar.mutate(borrador)}
          >
            {guardar.isPending ? "Guardando…" : "Guardar reglas"}
          </Boton>
        </div>
      ) : (
        <p className="mt-3 text-[11.5px] text-texto-3">
          Solo un administrador cambia estas reglas.
        </p>
      )}

      <Canales puedeAdministrar={puedeAdministrar} />

      <p className="mt-4 text-[11.5px] text-texto-3">
        Cada alerta queda en el registro de auditoría: cuándo se generó, a quién se avisó y cuándo
        se resolvió. Sirve como evidencia de control.
      </p>
    </Tarjeta>
  );
}

/**
 * Los interruptores de canal. Cada uno se guarda al momento: es un sí o un no, y esperar a un
 * botón "Guardar" haría creer que quedó apagado cuando todavía no. El aviso en la app no se
 * apaga: lo que se apaga es el correo.
 */
function Canales({ puedeAdministrar }: { puedeAdministrar: boolean }) {
  const trpc = useTRPC();
  const canales = useQuery(trpc.alertas.canales.queryOptions());
  const guardar = useMutation(
    trpc.alertas.guardarCanal.mutationOptions({ onSuccess: () => queryClient.invalidateQueries() }),
  );

  return (
    <div className="mt-5 border-t border-borde pt-4">
      <p className="mb-1 text-[12.5px] font-semibold text-texto">Avisar también por correo</p>
      <p className="mb-3 text-[11.5px] text-texto-3">
        La campana avisa siempre. Esto decide si además sale un correo.
      </p>
      {canales.data && (
        <div className="space-y-2">
          {TIPOS_NOTIFICACION.map((tipo) => (
            <label key={tipo} className="flex items-center gap-2 text-[12.5px]">
              <input
                type="checkbox"
                checked={canales.data[tipo]}
                disabled={!puedeAdministrar || guardar.isPending}
                onChange={(e) => guardar.mutate({ tipo, activo: e.target.checked })}
                className="size-[15px] accent-marino"
              />
              <span className="flex-1 text-texto-2">{NOMBRE_CANAL_CORREO[tipo]}</span>
            </label>
          ))}
        </div>
      )}
      {guardar.error && <Aviso className="mt-3">{mensajeDe(guardar.error)}</Aviso>}
    </div>
  );
}
